'use client';
import React from 'react';
import { Button } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { NumericFormat } from 'react-number-format';

export interface HYSACalculatorProps {
  title: string;
  subtitle?: string;
  calculator_link: string;
}

const inputStyles = {
  backgroundColor: ' #fefefe',
  border: '1px solid #e5e7eb',
  color: '#0a0a0a',
} as React.CSSProperties;

export const HYSACalculator: React.FC<HYSACalculatorProps> = ({ calculator_link, subtitle, title }) => {
  const [initialDeposit, setInitialDeposit] = React.useState(10000);
  const [recurringDeposit, setRecurringDeposit] = React.useState(100);
  const [interestRate, setInterestRate] = React.useState(4.25);
  const [period, setPeriod] = React.useState(12);
  const [periodType, setPeriodType] = React.useState('Months');
  const [interestEarned, setInterestEarned] = React.useState(0);
  const [totalBalace, setTotalBalace] = React.useState(0);

  const HYSAOptions = React.useMemo(() => {
    return { initialDeposit, interestRate, period, periodType, recurringDeposit };
  }, [initialDeposit, recurringDeposit, interestRate, period, periodType]);

  const handleHYSACalculation = React.useCallback(async filterOptions => {
    const r = filterOptions.interestRate / 100;
    const n = 12;

    //t is the time the money is invested or borrowed for, in years (12/12 = 1 year).
    let t = n / 12;
    let additionalDeposit = 0;
    if (filterOptions.periodType == 'Months') {
      t = filterOptions.period / 12;

      additionalDeposit = filterOptions.recurringDeposit * filterOptions.period;
    } else {
      additionalDeposit = filterOptions.recurringDeposit * filterOptions.period * 12;
      t = filterOptions.period;
    }

    const PMT = filterOptions.recurringDeposit;
    const s1 = Math.pow(1 + r / n, n * t);
    const p1 = filterOptions.initialDeposit * s1;

    const p2 = PMT * ((s1 - 1) / (r / n));
    const finalAmount = p1 + p2;

    const totalInterest = finalAmount - (filterOptions.initialDeposit + additionalDeposit);
    setTotalBalace(Number(finalAmount.toFixed(2)));
    setInterestEarned(Number(totalInterest.toFixed(2)));
  }, []);

  React.useEffect(() => {
    handleHYSACalculation({ ...HYSAOptions });
  }, [handleHYSACalculation, HYSAOptions]);

  const handleInitialDeposit = React.useCallback(
    (value: number) => {
      setInitialDeposit(Number(value));
      handleHYSACalculation({ ...HYSAOptions, initialDeposit: value });
    },
    [handleHYSACalculation, HYSAOptions],
  );

  const handleRecurringDeposit = React.useCallback(
    (value: number) => {
      setRecurringDeposit(Number(value));
      handleHYSACalculation({ ...HYSAOptions, recurringDeposit: value });
    },
    [handleHYSACalculation, HYSAOptions],
  );

  const handleInterestRateChange = React.useCallback(
    (value: number) => {
      setInterestRate(Number(value));
      handleHYSACalculation({ ...HYSAOptions, interestRate: value });
    },
    [handleHYSACalculation, HYSAOptions],
  );

  const handlePeriodChange = React.useCallback(
    (value: number) => {
      setPeriod(Number(value));
      handleHYSACalculation({ ...HYSAOptions, period: value });
    },
    [handleHYSACalculation, HYSAOptions],
  );

  const handleChangePeriodType = React.useCallback(
    (value: string) => {
      setPeriodType(value);
      handleHYSACalculation({ ...HYSAOptions, periodType: value });
    },
    [handleHYSACalculation, HYSAOptions],
  );

  return (
    <HYSACalculatorContainer>
      <div className="w-100 mb-5">
        <div className="wrap-block flex-col bg-white border border-t-4 shadow-sm rounded-xl">
          <div className="p-4 md:p-5">
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            {subtitle && <p className="mt-2 mb-4 text-gray-500">{subtitle}</p>}
            <div className="grid gap-4 grid-cols-12 grid-flow-row grid-rows-1 w-full justify-between max-width-1200-centered">
              <div className="md:col-span-6 col-span-12 w-full">
                <div className="input-container flex flex-col mb-2">
                  <label>Initial Deposit:</label>
                  <NumericFormat
                    className="border p-2 rounded"
                    onValueChange={item => {
                      handleInitialDeposit(Number(item.value));
                    }}
                    prefix={'$'}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                    value={10000}
                  />
                </div>
                <div className="input-container flex flex-col mb-2">
                  <label>Recurring Deposit (Monthly):</label>
                  <NumericFormat
                    className="border p-2 rounded"
                    onValueChange={item => {
                      handleRecurringDeposit(Number(item.value));
                    }}
                    prefix={'$'}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                    value={100}
                  />
                </div>
                <div className="input-container flex flex-col mb-2">
                  <label>Interest Rate (%) APY:</label>
                  <NumericFormat
                    className="border p-2 rounded"
                    onValueChange={item => {
                      handleInterestRateChange(Number(item.value));
                    }}
                    value={4.25}
                  />
                </div>
                <div className="input-container flex flex-col mb-2">
                  <label>Save For How Long:</label>
                  <div className="grid gap-1 grid-cols-12 grid-flow-row grid-rows-1 w-full justify-between">
                    <div className="col-span-6">
                      <NumericFormat
                        className="border p-2 rounded w-full"
                        decimalScale={2}
                        onValueChange={item => {
                          handlePeriodChange(Number(item.value));
                        }}
                        value={12}
                      />
                    </div>
                    <div className="col-span-6">
                      <select
                        className="appearance-none relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                        id="loan_program"
                        name="loan_program"
                        onChange={e => handleChangePeriodType(e.target.value)}
                        style={inputStyles}
                      >
                        <option value="Months">Months</option>
                        <option value="Years">Years</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-6 col-span-12 w-full">
                <div className="result-node flex-col mb-4">
                  <h5>
                    Total Interest Earned After {period} {periodType}
                  </h5>
                  <div className="price-item">${interestEarned.toLocaleString()}</div>
                </div>
                <div className="result-node flex-col mb-4">
                  <h5>
                    Total Ending Balance Including Interest After {period} {periodType}
                  </h5>
                  <div className="price-item">${totalBalace.toLocaleString()}</div>
                </div>
              </div>
            </div>
            {calculator_link && (
              <div className="action-btn">
                <Button as="a" href={calculator_link} size="md" variant="flat-orange">
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </HYSACalculatorContainer>
  );
};

export const HYSACalculatorContainer = styled.div`
  .wrap-block {
    border-top-color: ${({ theme }) => theme.colorPalette.orange500};
  }
  .input-container {
    label {
      font-weight: 500;
    }
  }
  .result-node {
    h5 {
      font-weight: 500;
    }
    .price-item {
      font-size: 2rem;
    }
  }
  .action-btn {
    a {
      padding: 0.75rem 1rem;
    }
  }
`;
